import React, { useRef, useState } from "react";
import { Upload, Loader2, Mic2, Music, Drum, Guitar, Radio, Crown, Scissors } from "lucide-react";
import { toast } from "sonner";
import { useStudio } from "@/studio/StudioContext";
import { useAuth } from "@/auth/AuthContext";
import { useSubscription } from "@/subscription/SubscriptionContext";
import SampleChopper from "@/studio/SampleChopper";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STEM_META = {
  instrumental: { label: "Instrumental", sub: "no vocals", icon: Music, color: "#00e5ff" },
  vocals: { label: "Vocals", sub: "acapella", icon: Mic2, color: "#ff007f" },
  drums: { label: "Drums", sub: "beat only", icon: Drum, color: "#ffd166" },
  bass: { label: "Bass", sub: "low end", icon: Radio, color: "#a855f7" },
  other: { label: "Melody", sub: "instruments", icon: Guitar, color: "#00ff88" },
};

export default function Sampler() {
  const { separateStems, loadToDeck, loadInstrumentalToDeck, addStemToCrate, chopSource, setChopSource } = useStudio();
  const { user, setShowAuth } = useAuth();
  const { isPro, openUpgrade } = useSubscription();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { stems, remaining, name }
  const [loadingDeck, setLoadingDeck] = useState("");
  const signedIn = !!(user && user.id);

  const onFile = async (file) => {
    if (!file) return;
    if (!signedIn) {
      setShowAuth(true);
      return;
    }
    if (!isPro) {
      openUpgrade("The Vocal Remover splits any song into vocals, drums, bass and instrumental.");
      return;
    }
    setBusy(true);
    setResult(null);
    toast.info("Splitting your track… this takes ~20–40 seconds.");
    try {
      const data = await separateStems(file);
      setResult(data);
      toast.success("Stems ready! Load any part onto a deck.");
    } catch (e) {
      if (e.response?.status === 402) {
        openUpgrade(e.response.data?.detail || "Upgrade to use the Vocal Remover.");
      } else if (e.response?.status === 401) {
        setShowAuth(true);
      } else {
        toast.error(e.response?.data?.detail || "Could not split this track, please try again.");
      }
    }
    setBusy(false);
  };

  const toDeck = async (deckId, key) => {
    if (!result) return;
    setLoadingDeck(`${deckId}-${key}`);
    try {
      if (key === "instrumental") {
        await loadInstrumentalToDeck(deckId, result.stems, result.name);
      } else {
        await loadToDeck(deckId, result.stems[key]);
      }
      toast.success(`Loaded ${STEM_META[key].label} on ${deckId === "deckA" ? "Deck A" : "Deck B"}`);
    } catch (_) {
      toast.error("Could not load that stem");
    }
    setLoadingDeck("");
  };

  const stemsToShow = result
    ? ["instrumental", "vocals", "drums", "bass", "other"].filter(
        (k) => k === "instrumental" || result.stems[k]
      )
    : [];

  return (
    <div className="h-full overflow-y-auto p-5 md:p-8" data-testid="sampler">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="font-display font-black text-2xl md:text-3xl tracking-wide flex items-center gap-2">
            <Scissors size={22} className="text-[#00e5ff]" /> Vocal Remover & Sampler
          </h1>
          <p className="text-neutral-500 text-sm mt-1">
            Drop in any song — AI splits it into <span className="text-[#00e5ff]">instrumental</span>, vocals, drums,
            bass and melody. Load any part onto a deck and flip it into your own beat.
            {!isPro && <span className="text-[#ffd166] ml-1">Pro feature.</span>}
          </p>
        </div>

        <button
          data-testid="sampler-upload-btn"
          onClick={() => (signedIn ? (isPro ? inputRef.current?.click() : openUpgrade("Vocal Remover is a Pro feature.")) : setShowAuth(true))}
          disabled={busy}
          className="w-full rounded-lg border-2 border-dashed border-[#2a2a2a] hover:border-[#00e5ff] transition-colors py-10 flex flex-col items-center justify-center gap-3 bg-[#0a0a0a] disabled:opacity-60"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            onFile(e.dataTransfer.files?.[0]);
          }}
        >
          {busy ? (
            <>
              <Loader2 size={30} className="text-[#00e5ff] animate-spin" />
              <span className="text-neutral-300 text-sm">Splitting your track…</span>
              <span className="text-neutral-600 text-xs">Hang tight, ~20–40 seconds</span>
            </>
          ) : (
            <>
              <Upload size={30} className="text-neutral-500" />
              <span className="text-neutral-200 text-sm font-semibold">Drop a song or click to upload</span>
              <span className="text-neutral-600 text-xs">MP3 or WAV {!isPro && "· Pro"}</span>
            </>
          )}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="audio/*"
          className="hidden"
          data-testid="sampler-file-input"
          onChange={(e) => {
            onFile(e.target.files?.[0]);
            e.target.value = "";
          }}
        />

        {result && (
          <div className="mt-6" data-testid="sampler-results">
            <div className="flex items-center justify-between mb-3">
              <span className="hw-label text-[10px] text-neutral-400 truncate">Stems for "{result.name}"</span>
              {typeof result.remaining === "number" && (
                <span className="text-[10px] text-neutral-500 flex items-center gap-1">
                  <Crown size={11} className="text-[#ffd166]" /> {result.remaining} splits left this month
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {stemsToShow.map((key) => {
                const m = STEM_META[key];
                const Icon = m.icon;
                const stem = key === "instrumental" ? null : result.stems[key];
                return (
                  <div
                    key={key}
                    data-testid={`stem-${key}`}
                    className="rounded-lg border border-[#262626] bg-[#0d0d0d] p-3 flex flex-col gap-3"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
                        style={{ background: `${m.color}22`, border: `1px solid ${m.color}55` }}
                      >
                        <Icon size={17} style={{ color: m.color }} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-neutral-100">{m.label}</div>
                        <div className="text-[10px] text-neutral-500">{m.sub}</div>
                      </div>
                    </div>
                    {stem && (
                      <audio
                        controls
                        src={`${BACKEND_URL}${stem.url}`}
                        className="w-full h-8"
                        data-testid={`stem-audio-${key}`}
                      />
                    )}
                    <div className="flex items-center gap-1.5">
                      <button
                        data-testid={`stem-deckA-${key}`}
                        onClick={() => toDeck("deckA", key)}
                        disabled={loadingDeck === `deckA-${key}`}
                        className="flex-1 h-8 rounded-sm text-[11px] font-bold flex items-center justify-center gap-1 disabled:opacity-50 transition-colors"
                        style={{ border: "1px solid #00e5ff55", color: "#00e5ff" }}
                      >
                        {loadingDeck === `deckA-${key}` ? <Loader2 size={12} className="animate-spin" /> : "→ Deck A"}
                      </button>
                      <button
                        data-testid={`stem-deckB-${key}`}
                        onClick={() => toDeck("deckB", key)}
                        disabled={loadingDeck === `deckB-${key}`}
                        className="flex-1 h-8 rounded-sm text-[11px] font-bold flex items-center justify-center gap-1 disabled:opacity-50 transition-colors"
                        style={{ border: "1px solid #ff007f55", color: "#ff007f" }}
                      >
                        {loadingDeck === `deckB-${key}` ? <Loader2 size={12} className="animate-spin" /> : "→ Deck B"}
                      </button>
                      {stem && (
                        <button
                          data-testid={`stem-crate-${key}`}
                          onClick={() => {
                            addStemToCrate(stem);
                            toast.success(`Added ${m.label} to crate`);
                          }}
                          className="h-8 px-2.5 rounded-sm text-[11px] font-bold border border-[#404040] text-neutral-300 hover:bg-[#1e1e1e] transition-colors"
                        >
                          + Crate
                        </button>
                      )}
                      {stem && (
                        <button
                          data-testid={`stem-chop-${key}`}
                          onClick={() => {
                            setChopSource({ url: stem.url, name: `${result.name} · ${m.label}` });
                            toast.success(`Loaded ${m.label} into the Sample Chopper`);
                          }}
                          className="h-8 px-2.5 rounded-sm text-[11px] font-bold border border-[#00e5ff55] text-[#00e5ff] hover:bg-[#00e5ff]/10 transition-colors flex items-center gap-1"
                        >
                          <Scissors size={11} /> Chop
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <SampleChopper source={chopSource} />
      </div>
    </div>
  );
}
